<script lang="ts">
  import { onMount } from 'svelte';
  import { useNavigate, useLocation } from 'svelte-navigator';
  import { ToastQueue, AppState, SetIconsBackground } from '../etc/stores';
  import { Container } from '../services/container';
  import { Auth } from '../services/auth';
  import Page from '../components/misc/Page.svelte';
  import Text from '../components/misc/Text.svelte';
  import Link from '../components/misc/Link.svelte';
  import Button from '../components/form/Button.svelte';
  import CodeInput from '../components/form/CodeInput.svelte';
  import Spinner from '../components/misc/Spinner.svelte';
  import BackArrow from '../components/misc/BackArrow.svelte';
  import { Logger } from '../services/logger';
  import { _ } from 'svelte-i18n';
  import type { ISignInOptions } from '../etc/types';
  import Analytics from '../services/analytics';
  import { extractMessageFromError } from '../services/api';
  import { WidgetConnect } from '../services/widget-connect';
  import { replaceVariables } from '../i18n';
  import Heading from '../components/typography/Heading.svelte';
  import Label from '../components/typography/Label.svelte';

  const auth = Container.get(Auth);
  const connect = Container.get(WidgetConnect);
  const logger = Container.get(Logger).context('Verification.svelte') as Logger;

  const navigate = useNavigate();
  const location = useLocation();

  const { providerData: customer = {} } = $AppState.auth;

  const hideBackButton: boolean = $location.state?.hideBackButton;
  const customMethod: ISignInOptions = $location.state?.method;
  const customMethodResend: () => Promise<string> = $location.state?.onResend;
  const resumedDescription: boolean = $location.state?.resumedDescription;

  const isCustomMethod = !!$location.state?.method;
  const method: ISignInOptions = isCustomMethod
    ? customMethod
    : {
        customerId: null,
        type: 'email',
        value: customer.email,
        name: customer.firstName || '',
        lastname: customer.lastName || '',
        firstCoins: null
      };

  let value;
  let loading = false;
  let resent = false;
  let loadingResent = false;
  let gaTitle = 'Verification' + Analytics.getFreeCoinsTitleFlow();

  const t = (text: string) => {
    return replaceVariables($_(`verification.${text}`), {
      MASK: method.maskedValue || method.value
    });
  };

  const onClick = async () => {
    try {
      loading = true;

      await auth.signin(value, method);
      await auth.finishAuthFlow(navigate);
    } catch (e) {
      logger.error(e);

      if (e.response.status === 400) {
        ToastQueue.add({
          title: t('errors.code_incorrect.title'),
          message: t('errors.code_incorrect.message'),
          type: 'error',
          duration: 5000
        });
      } else {
        ToastQueue.add({
          title: $_('error.title'),
          message: extractMessageFromError(e),
          type: 'error',
          duration: 5000
        });
      }
    } finally {
      loading = false;
    }
  };

  const resend = async () => {
    try {
      loadingResent = true;

      if (customMethodResend) {
        const id = await customMethodResend();
        if (method.validationCode) {
          method.validationCode.id = id;
        } else {
          method.validationCode = { id };
        }
      } else {
        const {
          data: { validationCode }
        } = await connect.getSignUpToken(
          $AppState.auth.token,
          $AppState.auth.account
        );
        method.validationCode = validationCode;
      }

      resent = true;

      setTimeout(() => {
        resent = false;
      }, 2000);
    } catch (e) {
      logger.error(e);

      ToastQueue.add({
        title: $_('error.title'),
        message: extractMessageFromError(e),
        type: 'error',
        duration: 5000
      });
    } finally {
      loadingResent = false;
    }
  };

  onMount(() => {
    SetIconsBackground('light');
    Analytics.logPageView(gaTitle, '/code');
  });
</script>

<Page enableScroll eventCategoryInCloseGA="signin-state">
  {#if !hideBackButton}
    <BackArrow />
  {/if}
  <div class="code__header">
    <Heading type="h2" textAlign="center">{t('header')}</Heading>
  </div>
  <div class="code__description">
    <Label type="default-regular" textAlign="center" color="#7A89B0">
      {@html t(resumedDescription ? 'description_resumed' : 'description')}
    </Label>
  </div>
  <div class="code__code">
    <CodeInput bind:value />
  </div>
  <div class="code__resend">
    <Text fontSize="12px">{t('code_resend')}</Text>
    <div class="code__resend__link" on:click={resend}>
      <Link>{t('code_resend_link')}</Link>
    </div>
  </div>
  <div class="code__feedback">
    <span class:hidden={!resent}>{t('feedback')}</span>
    <div class:hidden={!loadingResent}>
      <Spinner color="#6B6B6B" />
    </div>
  </div>
  <div class="code__button">
    <Button
      type="button"
      on:click={onClick}
      label={t('btn_next')}
      {loading}
      disabled={loading || value?.length !== 4}
    />
  </div>
</Page>

<style lang="scss">
  div {
    width: 100%;
  }
  .code {
    &__header {
      margin-top: 64px;
    }
    &__description {
      margin-top: 16px;
      b {
        font-weight: bold;
      }
      :global(p) {
        line-height: 24px;
      }
    }
    &__code {
      margin-top: 56px;
      display: flex;
      justify-content: center;
    }
    &__resend {
      margin-top: 40px;
      display: flex;
      justify-content: center;
      &__link {
        flex: 0;
        white-space: nowrap;
        margin-left: 5px;
      }
    }
    &__feedback {
      height: 0;
      padding-top: 8px;
      padding-bottom: 8px;
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      color: #193647;
      span {
        position: absolute;
        top: 6px;
        left: 50%;
        transform: translateX(-50%);
        height: 12px;
      }
      div {
        top: 0;
        display: flex;
        justify-content: center;
      }
      .hidden {
        opacity: 0;
      }
    }
    &__terms {
      height: 40px;
      p {
        font-size: 12px;
        line-height: 15px;
        font-weight: 400;
        text-align: center;
        a {
          color: var(--twism-theme-text);
          text-decoration: none;
        }
      }
    }
    &__button {
      margin-top: 52px;
    }
  }
</style>
